/**
 * PetCareExtras — compact "pet nerd" care log for dogs and cats, shown
 * only on the dedicated /pets page (never on the dashboard, to avoid
 * cluttering the day-to-day glance for families that don't care about
 * this detail).
 *
 * Renders four possible stamps:
 *   - Annual vet wellness (dogs + cats) — shows countdown to next
 *   - Flea/tick pill (dogs + cats) — next = last + interval_days
 *   - Bath (dogs only)
 *   - Litter change (cats, opt-in via PetDialog)
 *
 * One-tap "Logged today" chips call /api/pets/care-log which stamps the
 * pet member doc server-side. Interval + next-date + litter-enabled are
 * edited inside PetDialog (Care schedule block).
 */

import React, { useState, useEffect } from "react";
import { Stethoscope, Pill, Bath, Cat, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isPetCareLockedForPet } from "@/lib/petCareGate";
import { shouldPulseCareUpgrade, markCareUpgradePulsed } from "@/lib/walkUpgradePulse";

function daysBetween(fromISO, toDate = new Date()) {
  if (!fromISO) return null;
  try {
    const a = new Date(fromISO + "T00:00:00");
    const b = new Date(toDate);
    b.setHours(0, 0, 0, 0);
    return Math.floor((b - a) / 86400000);
  } catch {
    return null;
  }
}

function daysUntil(toISO) {
  if (!toISO) return null;
  try {
    const a = new Date();
    a.setHours(0, 0, 0, 0);
    const b = new Date(toISO + "T00:00:00");
    return Math.floor((b - a) / 86400000);
  } catch {
    return null;
  }
}

function addDaysISO(fromISO, days) {
  if (!fromISO) return null;
  try {
    const d = new Date(fromISO + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

// Tone the countdown so "overdue" shouts, "soon" whispers, "fine" is silent.
function toneFor(days) {
  if (days === null || days === undefined) return "neutral";
  if (days < 0) return "overdue";
  if (days <= 7) return "soon";
  return "ok";
}

function toneClass(tone) {
  switch (tone) {
    case "overdue":
      return "text-[color:var(--danger,#a14231)]";
    case "soon":
      return "text-[color:var(--ink-2)]";
    default:
      return "text-[color:var(--ink-3)]";
  }
}

function StampRow({ icon: Icon, label, subtitle, tone, busy, onLog, testid, readOnly }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <Icon className="w-4 h-4 text-[color:var(--ink-2)] shrink-0" strokeWidth={2} />
        <div className="min-w-0">
          <div className="text-sm text-[color:var(--ink)] truncate">{label}</div>
          <div className={`text-[12px] leading-tight truncate ${toneClass(tone)}`}>
            {subtitle}
          </div>
        </div>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={(e) => {
            // Stop the click from bubbling up to the enclosing
            // <section role='button'> on PetsPage, which would otherwise
            // yank the user back to /app and hide the updated countdown.
            e.stopPropagation();
            onLog();
          }}
          disabled={busy}
          className="ff-chip text-[12px]"
          data-testid={testid}
        >
          {busy ? "Saving…" : "Log"}
        </button>
      )}
    </div>
  );
}

export default function PetCareExtras({ pet, members, tier, viewerIsYoungKid, onPetUpdated, onOpenUpgrade }) {
  const species = pet.pet_species;
  const isDog = species === "dog";
  const isCat = species === "cat";
  const [busy, setBusy] = useState(null);

  // Tier gate computed BEFORE any early return so the pulse hooks
  // below stay above any conditional return — React rules-of-hooks.
  // Locked is a pure function of props and returns false for non-
  // dog/cat species, so this is cheap to compute on every render.
  const locked = (isDog || isCat) && isPetCareLockedForPet(pet, members, tier);
  // PetCareExtras and PetCareScheduleBlock share the same care-pulse
  // bucket — once one of them flashes the button for a given pet, the
  // other won't flash again. Keeps the intro feeling like a single
  // moment rather than two separate twitches on /pets.
  const [carePulse] = useState(() => locked && shouldPulseCareUpgrade(pet?.id));
  useEffect(() => {
    if (carePulse) markCareUpgradePulsed(pet?.id);
  }, [carePulse, pet?.id]);

  // Only dogs and cats get this whole block. For fish/birds/reptiles it
  // would just be noise — those pets are already well-covered by the
  // daily Water and Fed stats on the card itself.
  if (!isDog && !isCat) return null;

  if (locked) {
    const upgradeTo = tier === "free" ? "Plus" : "Grand";
    const reason =
      tier === "free"
        ? "Your Free plan includes the care log for your first dog or cat. Upgrade to Plus to add a second, Grand for three, or Extended for every dog & cat in the family."
        : "Your Plus plan includes the care log for two dogs or cats. Upgrade to Grand to unlock it for every pet.";
    return (
      <div
        className="mt-4 pt-3 border-t border-[color:var(--line-soft)]"
        data-testid={`pet-care-locked-${pet.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-3.5 h-3.5 text-[color:var(--ink-3)]" strokeWidth={2} />
          <div className="label-tiny">Care log · {upgradeTo} and up</div>
        </div>
        <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
          {reason}
        </p>
        {onOpenUpgrade && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenUpgrade();
            }}
            className={`ff-btn mt-3${carePulse ? " ff-care-upgrade-pulse" : ""}`}
            data-testid={`pet-care-upgrade-${pet.id}`}
          >
            Upgrade to {upgradeTo} →
          </button>
        )}
      </div>
    );
  }

  const log = async (kind) => {
    setBusy(kind);
    try {
      const updated = await api.logPetCareMilestone(pet.id, kind);
      onPetUpdated && onPetUpdated(updated);
      toast.success("Logged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(null);
    }
  };

  // --- Vet wellness ----------------------------------------------------
  const vetNext = pet.vet_wellness_next;
  const vetLast = pet.vet_wellness_last;
  const vetDays = daysUntil(vetNext);
  let vetSub;
  if (vetNext) {
    if (vetDays < 0) vetSub = `${Math.abs(vetDays)}d overdue`;
    else if (vetDays === 0) vetSub = "Due today";
    else if (vetDays <= 60) vetSub = `${vetDays}d to go`;
    else vetSub = `Next on ${vetNext}`;
  } else if (vetLast) {
    vetSub = `Last ${vetLast} · no next date set`;
  } else {
    vetSub = "Not logged yet";
  }

  // --- Flea/tick -------------------------------------------------------
  const fleaLast = pet.flea_tick_last;
  const fleaInterval = pet.flea_tick_interval_days || 30;
  const fleaNextISO = fleaLast ? addDaysISO(fleaLast, fleaInterval) : null;
  const fleaDays = daysUntil(fleaNextISO);
  let fleaSub;
  if (fleaLast) {
    if (fleaDays < 0) fleaSub = `${Math.abs(fleaDays)}d overdue · ${fleaInterval}d cycle`;
    else if (fleaDays === 0) fleaSub = `Due today · ${fleaInterval}d cycle`;
    else fleaSub = `Next in ${fleaDays}d · ${fleaInterval}d cycle`;
  } else {
    fleaSub = `Not logged · ${fleaInterval}d cycle`;
  }

  // --- Bath (dogs) -----------------------------------------------------
  let bathSub;
  if (pet.bath_last) {
    const d = daysBetween(pet.bath_last);
    if (d === 0) bathSub = "Earlier today";
    else if (d === 1) bathSub = "Yesterday";
    else bathSub = `${d}d ago`;
  } else {
    bathSub = "Not logged yet";
  }

  // --- Litter (cats, optional) -----------------------------------------
  let litterSub;
  if (pet.litter_last) {
    const d = daysBetween(pet.litter_last);
    if (d === 0) litterSub = "Earlier today";
    else if (d === 1) litterSub = "Yesterday";
    else litterSub = `${d}d ago`;
  } else {
    litterSub = "Not logged yet";
  }
  const showLitter = isCat && !!pet.litter_enabled;

  return (
    <div className="mt-4 pt-3 border-t border-[color:var(--line-soft)]">
      <div className="label-tiny mb-1">
        Care log{viewerIsYoungKid ? " · grown-ups only" : ""}
      </div>
      <div className="divide-y divide-[color:var(--line-soft)]">
        <StampRow
          icon={Stethoscope}
          label="Annual vet exam"
          subtitle={vetSub}
          tone={toneFor(vetDays)}
          busy={busy === "vet_wellness"}
          onLog={() => log("vet_wellness")}
          testid={`pet-care-vet-${pet.id}`}
          readOnly={viewerIsYoungKid}
        />
        <StampRow
          icon={Pill}
          label="Tick/flea treatment"
          subtitle={fleaSub}
          tone={toneFor(fleaDays)}
          busy={busy === "flea_tick"}
          onLog={() => log("flea_tick")}
          testid={`pet-care-flea-${pet.id}`}
          readOnly={viewerIsYoungKid}
        />
        {isDog && (
          <StampRow
            icon={Bath}
            label="Last bath"
            subtitle={bathSub}
            tone="neutral"
            busy={busy === "bath"}
            onLog={() => log("bath")}
            testid={`pet-care-bath-${pet.id}`}
            readOnly={viewerIsYoungKid}
          />
        )}
        {showLitter && (
          <StampRow
            icon={Cat}
            label="Litter change"
            subtitle={litterSub}
            tone="neutral"
            busy={busy === "litter"}
            onLog={() => log("litter")}
            testid={`pet-care-litter-${pet.id}`}
            readOnly={viewerIsYoungKid}
          />
        )}
      </div>
    </div>
  );
}
